/**
 * The jump the user does not have to click.
 *
 * If an answer carries a `navigate` block with `auto: true` (the user gave a
 * command: „öffne die API-Schlüssel"), the portal jumps by itself. This is
 * triggered from a **stream callback** — `processPayload` in `ChatExchange`,
 * `onDone` in the question panel — and no hook may live there. Hence the same
 * construction as for handoff and spotlight: a module store with listeners,
 * plus an effect in the component that holds `useGuideNavigate` and performs
 * the jump (CONTRACT.md §7.3).
 *
 * **Exactly once per message.** Idempotency hinges on a key provided by the
 * caller (conversation + message index + address), not on a boolean flag: a
 * stream restart (`resumeFromChunk`) delivers the same payload again, and
 * `<React.StrictMode>` runs effects twice in development. A second call with
 * the same key is therefore a no-op; a different one takes effect again.
 *
 * **Only from a running stream.** There is no way to jump from an old
 * conversation from here: requests are registered exclusively while reading a
 * payload, and that only happens while a stream is running. The renderer of
 * old messages (`ChatMessage`) does not touch this store — it shows the button.
 *
 * **The spotlight travels along.** If the same answer also carries a
 * `highlight` block, it belongs *after* the navigation: otherwise the search
 * deadline of `GuideSpotlight` (1.5 s) would start on the old page and might
 * be over before the target is rendered on the new one. That is why the
 * request carries the spotlight and the runner only requests it once
 * `guideNavigate` has returned.
 */

import { useEffect, useSyncExternalStore } from 'react';

import { requestSpotlight } from './spotlight';
import { isNavigableGuideHref, useGuideNavigate } from './useGuideNavigate';

/** Deadline within which a jump must be picked up. */
export const AUTO_NAVIGATE_TTL_MS = 6_000;

/** This many handled keys are remembered — more than enough for a session. */
const HANDLED_LIMIT = 50;

export interface AutoNavigateRequest {
    /** Unique per request. */
    id: string;
    /** Portal-internal address (`pathname + search`), already checked. */
    href: string;
    /** The caller's idempotency key. */
    key: string;
    /** Spotlight that is requested after arrival. */
    spotlight?: { guideId: string; label?: string; hint?: string };
    requestedAt: number;
    ttlMs: number;
}

let pending: AutoNavigateRequest | null = null;
let sequence = 0;
const handled: string[] = [];
const listeners = new Set<() => void>();

function publish(next: AutoNavigateRequest | null): void {
    if (pending === next) return;
    pending = next;
    listeners.forEach((listener) => listener());
}

/**
 * Requests a jump. A return value of `null` means "nothing requested": the
 * address is unusable or this key has already been handled. The caller uses
 * this to tell whether it has to request the spotlight itself.
 */
export function requestAutoNavigate(
    request: {
        href: string;
        key: string;
        spotlight?: { guideId: string; label?: string; hint?: string };
        ttlMs?: number;
    },
    now: number = Date.now(),
): AutoNavigateRequest | null {
    if (!isNavigableGuideHref(request.href)) return null;
    if (handled.indexOf(request.key) >= 0) return null;
    handled.push(request.key);
    if (handled.length > HANDLED_LIMIT) handled.splice(0, handled.length - HANDLED_LIMIT);

    const next: AutoNavigateRequest = {
        id: `auto-navigate-${now.toString(36)}-${(sequence += 1)}`,
        href: request.href,
        key: request.key,
        spotlight: request.spotlight,
        requestedAt: now,
        ttlMs: request.ttlMs ?? AUTO_NAVIGATE_TTL_MS,
    };
    publish(next);
    return next;
}

/** True while the request is within its deadline (also when the clock runs backwards). */
export function isAutoNavigateFresh(
    request: AutoNavigateRequest | null,
    now: number = Date.now(),
): boolean {
    if (!request) return false;
    const age = now - request.requestedAt;
    return age >= 0 && age < request.ttlMs;
}

/** The pending request, without consuming it. */
export function peekAutoNavigate(now: number = Date.now()): AutoNavigateRequest | null {
    return isAutoNavigateFresh(pending, now) ? pending : null;
}

/**
 * Picks up the request and clears it. Two mounted runners (main chat and
 * question panel can be in the document at the same time) thus share one
 * request: the first gets it, the second gets `null`.
 */
export function consumeAutoNavigate(now: number = Date.now()): AutoNavigateRequest | null {
    const claimed = peekAutoNavigate(now);
    publish(null);
    return claimed;
}

/** Tests only: hard-reset the request and the memory. */
export function resetAutoNavigate(): void {
    handled.length = 0;
    publish(null);
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Deliberately the **raw** module state, not the freshness check: a
 * `getSnapshot` that reads the clock would return different values within the
 * same render and send React into an infinite loop (see `chatHandoff.ts`,
 * `spotlight.ts`).
 */
function snapshot(): AutoNavigateRequest | null {
    return pending;
}

export function useAutoNavigate(): AutoNavigateRequest | null {
    return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * The runner: mount once in every chat shell (`ChatExchange`,
 * `OverlayBotChat`). It holds the hook that a stream callback cannot hold and
 * performs the jump in an effect — after the commit, not in the middle of
 * parsing a chunk.
 *
 * In the main chat this runs everything a click also triggers: handoff to the
 * question panel and morph (`useGuideNavigate`). In the panel there is no
 * handoff (it is not on `/chatbot`), but there is the self-navigation marker —
 * without it the panel would throw away its own conversation on arrival.
 */
export function useAutoNavigateRunner(): void {
    const guideNavigate = useGuideNavigate();
    const request = useAutoNavigate();

    useEffect(() => {
        if (!request) return;
        const claimed = consumeAutoNavigate();
        // Expired, or the other runner was faster.
        if (!claimed) return;
        // `spotlightOnSame` only if this run brings no spotlight of its own: the
        // registry fallback in `useGuideNavigate` comes from an `import()`, i.e.
        // one microtask **after** the call below — it would overwrite the more
        // precise spotlight of the `highlight` block.
        const result = guideNavigate(claimed.href, { spotlightOnSame: !claimed.spotlight });
        // Rejected means: no navigation happened. Then no spotlight either — it
        // would search for its target on the page the user stayed on, and in
        // the worst case find an element with the same name there (`nav.*` is
        // on every page).
        if (result === 'rejected') return;
        // Only now the spotlight: the search deadline should start on the
        // target page.
        if (claimed.spotlight) requestSpotlight(claimed.spotlight);
    }, [request, guideNavigate]);
}
